import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection
} from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideServiceWorker } from '@angular/service-worker';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { provideToastr } from 'ngx-toastr';
import {
  LucideAngularModule,
  Activity,
  AlertTriangle,
  ArrowLeft,
  ArrowUpRight,
  Ban,
  Bell,
  Briefcase,
  Building,
  Building2,
  Calendar,
  CalendarClock,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CircleAlert,
  CircleHelp,
  CircleUser,
  CircleUserRound,
  ClipboardList,
  Clock3,
  Database,
  Download,
  Edit,
  Eye,
  EyeOff,
  FileJson,
  FileText,
  Funnel,
  FunnelX,
  Heart,
  HeartPulse,
  Hospital,
  Hotel,
  Image,
  Info,
  KeyRound,
  Laptop,
  LayoutDashboard,
  Loader2,
  LockKeyhole,
  LogOut,
  Mail,
  MessageCircle,
  MessageSquare,
  Moon,
  MoreHorizontal,
  MoreVertical,
  Paperclip,
  Phone,
  PhoneCall,
  MapPin,
  Plus,
  Receipt,
  Scale,
  ScrollText,
  Search,
  Send,
  Settings,
  Sheet,
  Shield,
  ShieldAlert,
  ShieldPlus,
  SlidersHorizontal,
  Smile,
  Sparkles,
  Stethoscope,
  Sun,
  Target,
  ToggleRight,
  Trash2,
  TriangleAlert,
  Upload,
  User,
  UserCog,
  UserMinus,
  UserPlus,
  Users,
  UsersRound,
  X
} from 'lucide-angular';

import { routes } from './app.routes';
import { AppUpdateService } from './core/app-update.service';
import { ChunkLoadErrorHandler } from './core/chunk-load-error.handler';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import { initTelemetryContext } from './core/telemetry/telemetry-context';
import { apiBaseInterceptor } from './core/interceptors/api-base.interceptor';
import { ncSessionAuthInterceptor } from './core/interceptors/nc-session-auth.interceptor';
import { environment } from '../environments/environment';

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: ErrorHandler, useClass: ChunkLoadErrorHandler },
    { provide: APP_BASE_HREF, useValue: '/' },
    provideZoneChangeDetection({ eventCoalescing: true }),
    importProvidersFrom(
      LucideAngularModule.pick({
        LayoutDashboard,
        Loader2,
        MessageCircle,
        Users,
        Bell,
        Settings,
        CircleUser,
        CircleUserRound,
        CircleHelp,
        Shield,
        ShieldAlert,
        Building2,
        Sparkles,
        ArrowLeft,
        ArrowUpRight,
        Sun,
        Moon,
        Laptop,
        Activity,
        AlertTriangle,
        Database,
        MessageSquare,
        LogOut,
        Heart,
        HeartPulse,
        Hospital,
        Plus,
        Stethoscope,
        Eye,
        Edit,
        Download,
        Ban,
        MoreVertical,
        Search,
        Mail,
        X,
        ChevronDown,
        ChevronUp,
        ChevronRight,
        ChevronLeft,
        Building,
        Briefcase,
        Hotel,
        EyeOff,
        Sheet,
        FileJson,
        User,
        UserCog,
        UserPlus,
        UserMinus,
        UsersRound,
        LockKeyhole,
        KeyRound,
        Trash2,
        ScrollText,
        Info,
        FileText,
        Upload,
        FunnelX,
        Funnel,
        ToggleRight,
        Check,
        ShieldPlus,
        Calendar,
        CalendarClock,
        Clock3,
        TriangleAlert,
        CircleAlert,
        SlidersHorizontal,
        MoreHorizontal,
        Paperclip,
        PhoneCall,
        Phone,
        MapPin,
        Image,
        Smile,
        Send,
        Target,
        ClipboardList,
        Receipt,
        Scale
      })
    ),
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled'
      })
    ),
    provideHttpClient(
      withInterceptors([errorInterceptor, apiBaseInterceptor, ncSessionAuthInterceptor, authInterceptor])
    ),
    provideAnimations(),
    provideToastr(),
    provideEnvironmentNgxMask(),
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.enableServiceWorker,
      registrationStrategy: 'registerWhenStable:30000'
    }),
    provideAppInitializer(() => {
      inject(AppUpdateService).init();
    }),
    provideAppInitializer(initTelemetryContext)
  ]
};
